import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { RootStateOrAny, useDispatch, useSelector } from "react-redux";
import { Map } from 'immutable';
import moment from "moment";

import { FailedApplicants } from "../../templates/FailedApplicants";

import {
  resetIsUpdateSuccessful,
  setFailedActiveKeyRequest,
  fetchApplicantsListRequest,
  fetchTotalApplicantsRequest,
  fetchApplicantsAfterAYearCount,
} from "../../../redux/applicant/actions";
import { clearSpecificRequest } from "../../../redux/request/action";
import {
  FETCH_APPLICANTS_REQUEST,
  UPDATE_APPLICANT_REQUEST,
  SEARCH_APPLICANT_REQUEST,
  FETCH_APPLICANTS_LIST_REQUEST,
} from "../../../redux/applicant/actionTypes";
import { arrayToObject } from "../../../utils/helpers/transform";

const FailedApplicantsPage = () => {
  const history = useHistory();
  const location: any = useLocation();
  const dispatch = useDispatch();

  const failedTotalAllocator = [
    "newApplicant",
    "shortlisted",
    "technicalExam",
    "initialInterview",
    "psychologicalExam",
    "finalInterview",
  ];

  const failedPhaseAllocator = [
    "New Applicant",
    "Shortlisted",
    "Technical Exam",
    "Initial Interview",
    "Psychological Exam",
    "Final Interview",
  ];

  const [applicantsData, setApplicantsData] = useState<any>({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastElement, setLastElement] = useState(undefined);

  const activeKey = useSelector(
    (state: RootStateOrAny) => state.applicant.failedActiveKey
  );

  const applicantsList = useSelector(
    (state: RootStateOrAny) => state.requestStore?.[FETCH_APPLICANTS_LIST_REQUEST],
  );

  const afterAYearApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.invalidApplicantsTotal,
  );

  const totalApplicants = useSelector(
    (state: RootStateOrAny) => state.applicant.totalApplicants,
  );

  const onClickApplicant = (key: any) => {
    history.push(`/recruit/failed/${key}`);
  };

  const setFailedActiveKey = (key: string) => {
    dispatch(setFailedActiveKeyRequest(key));
  };

  const dispatchFetchFailedApplicants = useCallback(
    (failedStatus: string, lastElementDocs?: unknown, limit = 10, subsStatus = undefined, position = undefined) => {
      const where = [
        ['applicationStatus.failedPhase', '==', failedStatus],
        ['applicationStatus.currentStatus', '==', 'Failed'],
        ['applicationStatus.subStatus', 'in', subsStatus],
        ['positionApplied', 'in', position],
        ['isDeleted', '==', false],
      ];
      const orderBy: any = failedStatus === 'Final Interview' 
        ? [['applicationStatus.dateMoved', 'desc']] 
        : [['validUntil', 'desc'], ['applicationStatus.dateMoved', 'desc']];
      const whereQueryFailed: any = failedStatus === 'Final Interview' ? where : [ ...where,  ['validUntil', '>', moment().toDate()]];
      return dispatch(
        fetchApplicantsListRequest({
          where: whereQueryFailed,
          orderBy,
          limit,
          startAfter: lastElementDocs,
        }),
      );
    }, [dispatch]
  );

  const dispatchFetchAfterAYearApplicants = useCallback(
    (status: string, failedStatus: string) =>
      dispatch(
        fetchApplicantsAfterAYearCount({
          where: [
            ['applicationStatus.failedPhase', '==', failedStatus],
            ['applicationStatus.currentStatus', '==', 'Failed'],
            ['isDeleted', '==', false],
            ['validUntil', '<', moment().toDate()],
          ],
        }, status),
      ),
    [dispatch]
  );

  const dispatchFetchTotalApplicants = useCallback(
    () =>
      dispatch(fetchTotalApplicantsRequest()),
    [dispatch]
  );

  const handleOnChange = (pagination: any, filters: any) => {
    const { current: page, pageSize: currPageSize } = pagination;
    const filteredPosition = filters.position || undefined;
    const filteredStatus = filters.status || undefined;
    dispatchFetchTotalApplicants();
    if (filters.position || filters.status) {
      setCurrentPage(1);
    }

    setPageSize(currPageSize);
    if (pageSize === currPageSize) {
      if (page > 1) {
        if (currentPage + 1 >= page) {
          dispatchFetchFailedApplicants(failedPhaseAllocator[activeKey || 0], lastElement, currPageSize, filteredStatus, filteredPosition);
        } else {
          dispatchFetchFailedApplicants(failedPhaseAllocator[activeKey || 0], undefined, page * currPageSize, filteredStatus, filteredPosition);
        }
      } else {
        dispatchFetchFailedApplicants(failedPhaseAllocator[activeKey || 0], undefined, currPageSize, filteredStatus, filteredPosition);
      }
      setCurrentPage(page);
    } else {
      setCurrentPage(1);
      dispatchFetchFailedApplicants(failedPhaseAllocator[activeKey || 0], undefined, page * currPageSize, filteredStatus, filteredPosition);
    }
  };

  const handleOnTabClick = (key: string) => {
    dispatch(clearSpecificRequest(FETCH_APPLICANTS_LIST_REQUEST));
    setFailedActiveKey(key);
    setCurrentPage(1);
    dispatchFetchFailedApplicants(failedPhaseAllocator[+key]);
    if (!(+key === 5)) {
      dispatchFetchAfterAYearApplicants(`${failedTotalAllocator[+key]}Failed`, failedPhaseAllocator[+key])
    }
    dispatch(dispatchFetchTotalApplicants());
  };

  useEffect(() => {
    if (applicantsList && applicantsList?.response) {
      const lists = applicantsList.response.data;
      const arrToObj = Map(arrayToObject(lists));

      const filterApplicantsData: any = Object.values(applicantsData)
        .filter((values: any) => 
          values.applicationStatus.failedPhase === failedPhaseAllocator[activeKey || 0] &&
          values.applicationStatus.currentStatus === 'Failed',
        );

        const mapApplicantsData = Map(arrayToObject(filterApplicantsData));
        const failedExamApplicantsList = mapApplicantsData.mergeDeep(arrToObj);
        setApplicantsData(failedExamApplicantsList.toJS());
        setLastElement(applicantsList.response.lastElement);
    }
  }, [applicantsList, activeKey]);

  useEffect(() => {
    dispatch(dispatchFetchFailedApplicants(failedPhaseAllocator[activeKey || 0]));
    dispatch(dispatchFetchAfterAYearApplicants(`${failedTotalAllocator[activeKey || 0]}Failed`, failedPhaseAllocator[activeKey || 0]));
    dispatch(dispatchFetchTotalApplicants());
    dispatch(clearSpecificRequest(SEARCH_APPLICANT_REQUEST));
    dispatch(setFailedActiveKeyRequest(activeKey || 0));
    return () => {
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_REQUEST));
      dispatch(clearSpecificRequest(UPDATE_APPLICANT_REQUEST));
      dispatch(clearSpecificRequest(FETCH_APPLICANTS_LIST_REQUEST));
      dispatch(resetIsUpdateSuccessful());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.applicantId) {
      history.push(`/recruit/failed/${location.state?.applicantId}`);
    }
  }, [location, history]);

  const mappedApplicantsData =
    Object.values(applicantsData).map((applicant: any, index: number) => {
      const status =
        !applicant.applicationStatus?.subStatus ||
        applicant.applicationStatus?.subStatus === "Read" ||
        applicant.applicationStatus?.subStatus === "Unread"
          ? ""
          : applicant.applicationStatus?.subStatus;
      const applicantName = applicant.applicantName
        ? `${applicant.applicantName.firstName} ${applicant.applicantName.middleName} ${applicant.applicantName.lastName}`
        : applicant.fullName;
      return {
        key: applicant.id,
        name: applicantName,
        position: applicant.positionApplied,
        dateMoved: applicant.applicationStatus.dateMoved
          ? `${moment(
              applicant.applicationStatus.dateMoved.toDate()
            ).format("MM/DD/YYYY hh:mm a")}`
          : null,
        dateApplied: `${moment(applicant.createdAt.toDate()).format(
          "MM/DD/YYYY hh:mm a"
        )}`,
        status: status || "Unassigned",
        reason: applicant.applicationStatus.reason || "",
        referredBy: applicant.referredBy,
      };
    }).sort(
      (a: any, b: any) => {
        const date1: any = new Date(a.dateMoved);
        const date2: any = new Date(b.dateMoved);
        return (date2 - date1);
      },
    );

  const total = (totalApplicants?.[failedTotalAllocator[activeKey || 0]]?.failed || 0) - (afterAYearApplicants?.[`${failedTotalAllocator[activeKey || 0]}Failed`] || 0);

  return (
    <FailedApplicants
      tableData={mappedApplicantsData}
      isLoading={applicantsList?.isLoading}
      onClickApplicant={onClickApplicant}
      defaultActiveKey={activeKey || "0"}
      onTabClick={handleOnTabClick}
      onChange={handleOnChange}
      total={total}
      currentPage={currentPage}
    />
  );
};

export default FailedApplicantsPage;
